import dayjs from "dayjs";
import { DEFAULT_TIME_RANGE, RANGE_TYPE_LABELS } from "./constants";
import Highcharts from "highcharts/highstock";

export function getRange(value = DEFAULT_TIME_RANGE) {
  const now = dayjs();
  switch (value) {
    case RANGE_TYPE_LABELS.HOUR_1:
      return [now.subtract(1, "hour"), now];
    case RANGE_TYPE_LABELS.HOUR_2:
      return [now.subtract(2, "hour"), now];
    case RANGE_TYPE_LABELS.HOUR_12:
      return [now.subtract(12, "hour"), now];
    case RANGE_TYPE_LABELS.HOUR_24:
      return [now.subtract(24, "hour"), now];
    case RANGE_TYPE_LABELS.DAY_1:
      return [now.startOf("day"), now];
    case RANGE_TYPE_LABELS.DAY_MINUS_1: {
      const yesterday = now.subtract(1, "day");
      return [yesterday.startOf("day"), yesterday.endOf("day")];
    }
    case RANGE_TYPE_LABELS.DAY_7:
      return [now.subtract(6, "day").startOf("day"), now];
    case RANGE_TYPE_LABELS.CUSTOM:
    default:
      return [now.subtract(12, "hour"), now];
  }
}
export function applyTheme(theme) {
  document.body.classList.remove("theme-light", "theme-dark");
  document.documentElement.classList.remove("light", "dark");
  document.body.classList.add(`theme-${theme}`);
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.classList.add(theme);
}

export const getDateRangeFormat = range => {
  return Array.isArray(range) && range.length > 0 ? range.map(d => d.format("MMM-DD, YYYY, hh:mm A")).join(" - ") : "";
};

// utils/transformChargerData.js
export function transformChargerData(apiData = []) {
  return {
    siteProduction: apiData.map(item => [
      new Date(item.timestamp).getTime(),
      item?.calculationMetrics?.siteProduction ?? 0,
    ]),
    evseConsumption: apiData.map(item => [
      new Date(item.timestamp).getTime(),
      item?.calculationMetrics?.evseConsumption ?? 0,
    ]),
    setpointPower: apiData.map(item => [
      new Date(item.timestamp).getTime(),
      item?.schedule?.[0]?.value?.setpointPower ?? 0,
      item?.calculationMetrics,
    ]),
  };
}

export function getEnergyConsumptionDateFormat(date) {
  return (
    date.getFullYear().toString() +
    String(date.getMonth() + 1).padStart(2, "0") +
    String(date.getDate()).padStart(2, "0")
  );
}

export function transformEnergyConsumptionData(data, startDate, endDate, startTime, endTime) {
  if (!Array.isArray(data) || !startTime) return [];

  const FIFTEEN_MIN_MS = 15 * 60 * 1000;

  const baseDay = startTime.startOf("day");
  const baseTs = baseDay.valueOf();

  const startEpoch = startTime.valueOf();
  const endEpoch = endTime.valueOf();

  let filteredData = data;
  if (startDate === endDate) {
    const now = dayjs();

    let points = Math.floor(now.diff(baseDay, "minute") / 15);

    if (!Number.isFinite(points) || points <= 0) {
      points = data.length;
    } else if (points > data.length) {
      points = data.length;
    }

    filteredData = data.slice(-points);
  }

  const built = filteredData.map((value, index) => {
    const ts = baseTs + index * FIFTEEN_MIN_MS;
    return [ts, value];
  });

  const finalData = built.filter(([ts]) => {
    return ts >= startEpoch && ts <= endEpoch;
  });
  return finalData;
}

export const getMaxTime = date => {
  const now = dayjs();
  if (date && dayjs(date).isSame(now, "day")) {
    return now;
  }
  return dayjs().endOf("day");
};

export function formatDate(value) {
  if (value === null || value === undefined || value === "") return "-";
  // Pre-clean common non-ISO suffixes like [UTC] so Date can parse the string
  let cleaned = value;
  try {
    if (typeof cleaned === "string") {
      // remove trailing bracketed zone like [UTC]
      cleaned = cleaned.replace(/\[.*\]$/, "").trim();
      // if still contains a space-separated timezone token at end (e.g. "...Z UTC"), drop it
      const parts = cleaned.split(" ");
      if (parts.length > 1 && /[A-Za-z]{2,}/.test(parts[parts.length - 1])) {
        parts.pop();
        cleaned = parts.join(" ");
      }
    }
  } catch (e) {
    // fall through to parse attempt
  }

  // Try to construct a Date and if invalid, fall back to the raw value
  const d = new Date(cleaned);
  if (isNaN(d.getTime())) return String(value);
  // Format: DD/MM/YYYY HH:MM:SS TIMEZONE (timezone as GMT±HH:MM)
  const pad = n => String(n).padStart(2, "0");
  const DD = pad(d.getDate());
  const MM = pad(d.getMonth() + 1);
  const YYYY = d.getFullYear();
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  // timezone offset in minutes; positive east of UTC
  const offsetMinutes = -d.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const offHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
  const offMins = pad(Math.abs(offsetMinutes) % 60);
  const tz = `GMT${sign}${offHours}:${offMins}`;
  return `${DD}/${MM}/${YYYY} ${hh}:${mm}:${ss} ${tz}`;
}

export const initModule = module => {
  if (typeof module === "function") {
    module(Highcharts);
  } else if (module && module.default) {
    module.default(Highcharts);
  }
};
